import React, { useEffect, useState } from 'react';
import { Loader2, UploadCloud, FileCheck2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService, getDefaultRouteForRole, isVerificationComplete, setUserProfile } from '../services/auth.service';
import { showAppAlert } from '../utils/appAlert';
import { buildPublicFileUrl } from '../utils/fileUrl';

const identificationOptions = [
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'NATIONAL_ID', label: 'National ID' }
];

const AccountVerification = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState(localStorage.getItem('phone') || '');
  const [identificationType, setIdentificationType] = useState(localStorage.getItem('identificationType') || '');
  const [identificationNumber, setIdentificationNumber] = useState(localStorage.getItem('identificationNumber') || '');
  const [identificationFile, setIdentificationFile] = useState(null);
  const [partnershipFile, setPartnershipFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingDetails, setSavingDetails] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);
  const [uploadingPartnership, setUploadingPartnership] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authService.getVerificationStatus();
      setStatus(response);
      setPhone(response.phone || localStorage.getItem('phone') || '');
      setIdentificationType(response.identificationType || '');
      setIdentificationNumber(response.identificationNumber || '');
      setUserProfile({
        customerType: response.customerType,
        phone: response.phone,
        identificationType: response.identificationType,
        identificationNumber: response.identificationNumber,
        identificationProofUploaded: response.identificationProofUploaded,
        partnershipContractUploaded: response.partnershipContractUploaded,
        verificationComplete: response.verificationComplete,
        requiresVerification: !response.verificationComplete
      });
    } catch (err) {
      setError(err.message || 'Failed to load verification details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVerificationComplete() && !localStorage.getItem('userCapabilities')?.includes('CAR_OWNER')) {
      navigate(getDefaultRouteForRole(localStorage.getItem('userRole')), { replace: true });
      return;
    }

    loadStatus();
  }, [navigate]);

  const handleSaveIdentificationType = async () => {
    if (!phone.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    if (!identificationType) {
      setError('Please select your identification type first.');
      return;
    }
    if (!identificationNumber.trim()) {
      setError('Please enter your identification number.');
      return;
    }
    if (identificationType === 'NATIONAL_ID' && !/^\d{16}$/.test(identificationNumber.trim())) {
      setError('National ID number must contain exactly 16 digits.');
      return;
    }

    setSavingDetails(true);
    setError('');
    try {
      const response = await authService.updateVerificationDetails(identificationType, identificationNumber.trim(), phone.trim());
      setStatus(response);
      setUserProfile({
        customerType: response.customerType,
        phone: response.phone,
        identificationType: response.identificationType,
        identificationNumber: response.identificationNumber,
        identificationProofUploaded: response.identificationProofUploaded,
        partnershipContractUploaded: response.partnershipContractUploaded,
        verificationComplete: response.verificationComplete,
        requiresVerification: !response.verificationComplete
      });
      showAppAlert({ type: 'success', title: 'Saved', message: 'Identification type updated successfully.' });
    } catch (err) {
      setError(err.message || 'Failed to update identification type.');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleUploadIdentification = async () => {
    if (!identificationFile) {
      setError('Please choose your identification proof document.');
      return;
    }

    setUploadingId(true);
    setError('');
    try {
      const response = await authService.uploadIdentificationProof(identificationFile);
      setStatus(response);
      setIdentificationFile(null);
      setUserProfile({
        customerType: response.customerType,
        phone: response.phone,
        identificationType: response.identificationType,
        identificationNumber: response.identificationNumber,
        identificationProofUploaded: response.identificationProofUploaded,
        partnershipContractUploaded: response.partnershipContractUploaded,
        verificationComplete: response.verificationComplete,
        requiresVerification: !response.verificationComplete
      });
      showAppAlert({ type: 'success', title: 'Uploaded', message: 'Identification proof uploaded successfully.' });
    } catch (err) {
      setError(err.message || 'Failed to upload identification proof.');
    } finally {
      setUploadingId(false);
    }
  };

  const handleUploadPartnership = async () => {
    if (!partnershipFile) {
      setError('Please choose the partnership contract document.');
      return;
    }

    setUploadingPartnership(true);
    setError('');
    try {
      const response = await authService.uploadPartnershipContract(partnershipFile);
      setStatus(response);
      setPartnershipFile(null);
      setUserProfile({
        customerType: response.customerType,
        phone: response.phone,
        identificationType: response.identificationType,
        identificationNumber: response.identificationNumber,
        identificationProofUploaded: response.identificationProofUploaded,
        partnershipContractUploaded: response.partnershipContractUploaded,
        verificationComplete: response.verificationComplete,
        requiresVerification: !response.verificationComplete
      });
      showAppAlert({ type: 'success', title: 'Uploaded', message: 'Partnership contract uploaded successfully.' });
    } catch (err) {
      setError(err.message || 'Failed to upload partnership contract.');
    } finally {
      setUploadingPartnership(false);
    }
  };

  useEffect(() => {
    if (status?.verificationComplete && (!status.partnershipContractRequired || status.partnershipContractUploaded)) {
      navigate(getDefaultRouteForRole(status.customerType || localStorage.getItem('userRole')), { replace: true });
    }
  }, [navigate, status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="flex items-center gap-3 text-brand-black font-semibold">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading verification details...
        </div>
      </div>
    );
  }

  const isOwner = status?.partnershipContractRequired || status?.customerType === 'CAR_OWNER';

  return (
    <div className="min-h-screen bg-[#F7F7F5] py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <p className="text-xs font-black tracking-[0.25em] uppercase text-gray-500">Account Verification</p>
          <h1 className="text-3xl sm:text-4xl font-black text-brand-black mt-3">Complete your verification</h1>
          <p className="text-sm text-gray-600 mt-3 max-w-2xl leading-6">
            Before you can continue using Go Rides, upload your identification proof.
            {isOwner ? ' Car owners must also upload a signed partnership contract.' : ''}
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[28px] border border-gray-200 bg-white p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-brand-black text-white flex items-center justify-center">
                <FileCheck2 className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-black text-brand-black">Identity details</h2>
                <p className="text-sm text-gray-500">Choose how you want to verify yourself.</p>
              </div>
            </div>

            <label className="block text-sm font-bold text-brand-black mb-2">Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setError('');
              }}
              placeholder="Enter your phone number"
              className="w-full rounded-2xl border border-gray-200 px-4 py-3.5 text-sm font-semibold text-brand-black focus:outline-none focus:ring-2 focus:ring-[#111111]"
            />
            {(status?.phoneRequired || !status?.phone) && (
              <p className="mt-2 text-xs font-semibold text-gray-500">A phone number is required before you can start using the platform.</p>
            )}

            <label className="block text-sm font-bold text-brand-black mt-5 mb-2">Identification Type</label>
            <select
              value={identificationType}
              onChange={(e) => {
                setIdentificationType(e.target.value);
                if (e.target.value === 'NATIONAL_ID') {
                  setIdentificationNumber((current) => current.replace(/\D/g, '').slice(0, 16));
                }
                setError('');
              }}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3.5 text-sm font-semibold text-brand-black focus:outline-none focus:ring-2 focus:ring-[#111111]"
            >
              <option value="">Select identification type</option>
              {identificationOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            <label className="block text-sm font-bold text-brand-black mt-5 mb-2">Identification Number</label>
            <input
              type="text"
              value={identificationNumber}
              onChange={(e) => {
                const nextValue = identificationType === 'NATIONAL_ID'
                  ? e.target.value.replace(/\D/g, '').slice(0, 16)
                  : e.target.value;
                setIdentificationNumber(nextValue);
                setError('');
              }}
              placeholder={identificationType === 'NATIONAL_ID' ? 'Enter 16-digit national ID number' : 'Enter passport number'}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3.5 text-sm font-semibold text-brand-black focus:outline-none focus:ring-2 focus:ring-[#111111]"
            />
            {identificationType === 'NATIONAL_ID' && (
              <p className="mt-2 text-xs font-semibold text-gray-500">National ID must contain exactly 16 digits.</p>
            )}

            <button
              type="button"
              onClick={handleSaveIdentificationType}
              disabled={savingDetails}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-black px-5 py-3 text-sm font-black text-white disabled:opacity-60"
            >
              {savingDetails && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Verification Details
            </button>

            <div className="mt-8 rounded-3xl border border-dashed border-gray-300 bg-[#FAFAFA] p-5">
              <p className="text-sm font-black text-brand-black">Proof Document</p>
              <p className="text-sm text-gray-500 mt-1">Upload a clear scan or photo of your selected ID document.</p>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setIdentificationFile(e.target.files?.[0] || null)}
                className="mt-4 block w-full text-sm text-gray-600 file:mr-4 file:rounded-xl file:border-0 file:bg-brand-black file:px-4 file:py-2.5 file:text-sm file:font-bold file:text-white"
              />
              <button
                type="button"
                onClick={handleUploadIdentification}
                disabled={uploadingId}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl border border-brand-black px-5 py-3 text-sm font-black text-brand-black disabled:opacity-60"
              >
                {uploadingId ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                Upload Proof Document
              </button>
            </div>

            {isOwner && (
              <div className="mt-6 rounded-3xl border border-dashed border-gray-300 bg-[#FAFAFA] p-5">
                <p className="text-sm font-black text-brand-black">Partnership Contract</p>
                <p className="text-sm text-gray-500 mt-1">Car owners must upload the signed partnership contract to finish verification.</p>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => setPartnershipFile(e.target.files?.[0] || null)}
                  className="mt-4 block w-full text-sm text-gray-600 file:mr-4 file:rounded-xl file:border-0 file:bg-brand-black file:px-4 file:py-2.5 file:text-sm file:font-bold file:text-white"
                />
                <button
                  type="button"
                  onClick={handleUploadPartnership}
                  disabled={uploadingPartnership}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl border border-brand-black px-5 py-3 text-sm font-black text-brand-black disabled:opacity-60"
                >
                  {uploadingPartnership ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                  Upload Partnership Contract
                </button>
              </div>
            )}
          </div>

          <div className="rounded-[28px] border border-gray-200 bg-brand-black p-6 sm:p-8 text-white">
            <p className="text-xs font-black uppercase tracking-[0.25em] text-white/60">Checklist</p>
            <h2 className="mt-3 text-2xl font-black">What is still needed?</h2>

            <div className="mt-8 space-y-4">
              <div className={`rounded-2xl border px-4 py-4 ${status?.identificationType ? 'border-emerald-400/40 bg-emerald-500/10' : 'border-white/15 bg-white/5'}`}>
                <p className="text-sm font-black">Identification type selected</p>
                <p className="text-sm text-white/70 mt-1">{status?.identificationType ? identificationOptions.find((item) => item.value === status.identificationType)?.label || status.identificationType : 'Not selected yet'}</p>
              </div>

              <div className={`rounded-2xl border px-4 py-4 ${status?.identificationNumber ? 'border-emerald-400/40 bg-emerald-500/10' : 'border-white/15 bg-white/5'}`}>
                <p className="text-sm font-black">Identification number</p>
                <p className="text-sm text-white/70 mt-1">{status?.identificationNumber || 'Not provided yet'}</p>
              </div>

              <div className={`rounded-2xl border px-4 py-4 ${status?.identificationProofUploaded ? 'border-emerald-400/40 bg-emerald-500/10' : 'border-white/15 bg-white/5'}`}>
                <p className="text-sm font-black">Identification proof</p>
                <p className="text-sm text-white/70 mt-1">{status?.identificationProofUploaded ? 'Uploaded successfully' : 'Upload required'}</p>
                {status?.identificationProofPath && (
                  <a
                    href={buildPublicFileUrl(status.identificationProofPath)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex mt-3 text-sm font-bold underline underline-offset-4"
                  >
                    View current file
                  </a>
                )}
              </div>

              {isOwner && (
                <div className={`rounded-2xl border px-4 py-4 ${status?.partnershipContractUploaded ? 'border-emerald-400/40 bg-emerald-500/10' : 'border-white/15 bg-white/5'}`}>
                  <p className="text-sm font-black">Partnership contract</p>
                  <p className="text-sm text-white/70 mt-1">{status?.partnershipContractUploaded ? 'Uploaded successfully' : 'Upload required for car owners'}</p>
                  {status?.partnershipContractPath && (
                    <a
                      href={buildPublicFileUrl(status.partnershipContractPath)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex mt-3 text-sm font-bold underline underline-offset-4"
                    >
                      View current file
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="mt-8 rounded-2xl bg-white text-brand-black px-5 py-4">
              <p className="text-xs font-black uppercase tracking-[0.25em] text-gray-500">Status</p>
              <p className="mt-2 text-lg font-black">
                {status?.verificationComplete ? 'Verification complete' : 'Verification pending'}
              </p>
              <p className="mt-2 text-sm text-gray-600">
                {status?.verificationComplete
                  ? 'Everything is ready. Redirecting you to your dashboard.'
                  : 'Complete all required uploads to continue using your account.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountVerification;
